import React from 'react';

interface NetworkProps {
  onLaunchPlatform?: () => void;
}

const Network: React.FC<NetworkProps> = ({ onLaunchPlatform }) => {

  return (
    <div className="bg-[#0a0a0e] text-white min-h-screen overflow-hidden">
      {/* Gradient Backgrounds */}
      <div className="fixed inset-0 overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#24c35b]/5 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-[#24c35b]/5 via-transparent to-transparent"></div>
        
        {/* Main Green Glows */}
        <div className="absolute top-1/4 -left-[10%] w-[500px] h-[500px] rounded-full bg-[#24c35b]/5 blur-[150px]"></div>
        <div className="absolute bottom-1/4 -right-[10%] w-[600px] h-[600px] rounded-full bg-[#24c35b]/5 blur-[150px]"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 z-10 pt-12">
        <div className="max-w-4xl mx-auto w-full text-center">
          {/* EKKO Logo */}
          <div className="mb-6">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-[#24c35b]/20 rounded-full blur-[40px]"></div>
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png" 
                alt="EKKO" 
                className="relative h-24 w-auto mx-auto"
              />
            </div>
          </div>

          <div className="inline-flex items-center bg-[#0d0d12] rounded-full px-3 py-1 mb-4 border border-[#24c35b]/20">
            <div className="w-2 h-2 rounded-full bg-[#24c35b] mr-2"></div>
            <span className="text-sm">Strategic Partnerships</span>
          </div>
            
          <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
            <span className="text-[#24c35b]">Partners</span>
          </h1>
            
          <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
            EKKO v2 is built on strong collaborations with industry leaders to deliver the best possible experience for our users. Explore our strategic partners.
          </p>
          
          <div className="flex gap-4 mb-8 justify-center">
            <button 
              onClick={onLaunchPlatform}
              className="bg-[#24c35b] hover:bg-[#1eaa4e] text-black font-medium px-6 py-3 rounded-lg transition-all duration-300 flex items-center"
            >
              Launch Platform
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <button 
              className="border border-gray-700 hover:border-[#24c35b]/50 text-white px-6 py-3 rounded-lg transition-all duration-300"
            >
              View Partnerships
            </button>
          </div>
            
          {/* Network Partners */}
          <div className="flex flex-col items-center mt-4">
            <div className="text-sm text-gray-400 mb-4">Integrated Partners</div>
            <div className="flex items-center gap-8 opacity-70">
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/jupiter.png" 
                alt="Jupiter" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/Solana.png" 
                alt="Solana" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/raydium.png" 
                alt="Raydium" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/pumpfun.png" 
                alt="Pump.fun" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/pkin.png" 
                alt="PKIN" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/nvidia.png" 
                alt="NVIDIA" 
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Wallet Partners Section */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Wallet <span className="text-[#24c35b]">Partners</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Seamless integrations with leading Solana wallets for a smooth user experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-8 bg-gradient-to-br from-purple-500/20 to-[#16161a] rounded-2xl border border-purple-500/30 text-center">
              <div className="w-20 h-20 bg-purple-500/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-purple-400 mb-4">Phantom</h3>
              <p className="text-gray-300">The most popular Solana wallet with seamless DeFi integration</p>
            </div>

            <div className="p-8 bg-gradient-to-br from-orange-500/20 to-[#16161a] rounded-2xl border border-orange-500/30 text-center">
              <div className="w-20 h-20 bg-orange-500/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-orange-400 mb-4">Solflare</h3>
              <p className="text-gray-300">Advanced Solana wallet with comprehensive portfolio management</p>
            </div>
          </div>
        </div>
      </section>

      {/* Exchange Partners Section */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Exchange <span className="text-[#24c35b]">Partners</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Strategic partnerships with both centralized and decentralized exchanges to maximize EKKO token accessibility.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="p-8 bg-gradient-to-br from-blue-500/20 to-[#16161a] rounded-2xl border border-blue-500/30 text-center max-w-md">
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/raydium.png" 
                alt="Raydium" 
                className="h-20 w-auto mx-auto mb-6"
              />
              <h3 className="text-2xl font-bold text-blue-400 mb-4">Raydium</h3>
              <p className="text-gray-300">Leading automated market maker and liquidity provider on Solana</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Partners Section */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Core <span className="text-[#24c35b]">Infrastructure</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Solana Foundation */}
            <div className="p-8 bg-gradient-to-br from-[#24c35b]/20 to-[#16161a] rounded-2xl border border-[#24c35b]/30">
              <div className="text-center mb-6">
                <img 
                  src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/Solana.png" 
                  alt="Solana" 
                  className="h-20 w-auto mx-auto mb-4"
                />
                <h3 className="text-2xl font-bold text-[#24c35b] mb-4">Solana Foundation</h3>
                <p className="text-gray-300 mb-6">
                  Leveraging Solana's high-performance blockchain to provide lightning-fast transactions and minimal fees for our users.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-300">
                  <div className="w-2 h-2 bg-[#24c35b] rounded-full mr-3"></div>
                  Sub-second transaction finality
                </div>
                <div className="flex items-center text-sm text-gray-300">
                  <div className="w-2 h-2 bg-[#24c35b] rounded-full mr-3"></div>
                  Eco-friendly blockchain
                </div>
                <div className="flex items-center text-sm text-gray-300">
                  <div className="w-2 h-2 bg-[#24c35b] rounded-full mr-3"></div>
                  Technical integration and development support
                </div>
              </div>
            </div>

            {/* Jupiter */}
            <div className="p-8 bg-gradient-to-br from-purple-500/20 to-[#16161a] rounded-2xl border border-purple-500/30">
              <div className="text-center mb-6">
                <img 
                  src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/jupiter.png" 
                  alt="Jupiter" 
                  className="h-20 w-auto mx-auto mb-4"
                />
                <h3 className="text-2xl font-bold text-purple-400 mb-4">Jupiter</h3>
                <p className="text-gray-300 mb-6">
                  Integrating Jupiter's liquidity aggregation protocol to provide our users with the best possible swap rates across the Solana ecosystem.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-300">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                  Access to deep liquidity across multiple DEXes
                </div>
                <div className="flex items-center text-sm text-gray-300">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                  Optimal routing for best execution prices
                </div>
                <div className="flex items-center text-sm text-gray-300">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                  Minimal slippage for large trades
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Become a Partner Section */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Become a <span className="text-[#24c35b]">Partner</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Interested in collaborating with EKKO v2? We're always looking for strategic partnerships that can enhance the ecosystem and provide value to our users.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="p-6 bg-[#16161a]/50 rounded-2xl border border-[#24c35b]/20 text-center">
              <div className="w-16 h-16 bg-[#24c35b]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#24c35b]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#24c35b] mb-3">Integration</h3>
              <p className="text-gray-300 text-sm">Integrate your product or service with EKKO v2 for a seamless user experience.</p>
            </div>

            <div className="p-6 bg-[#16161a]/50 rounded-2xl border border-[#24c35b]/20 text-center">
              <div className="w-16 h-16 bg-[#24c35b]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#24c35b]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#24c35b] mb-3">Technology</h3>
              <p className="text-gray-300 text-sm">Collaborate with us to build innovative solutions on our platform.</p>
            </div>

            <div className="p-6 bg-[#16161a]/50 rounded-2xl border border-[#24c35b]/20 text-center">
              <div className="w-16 h-16 bg-[#24c35b]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#24c35b]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#24c35b] mb-3">Marketing Partners</h3>
              <p className="text-gray-300 text-sm">Join forces to expand our reach and bring DeFi to more users worldwide.</p>
            </div>
          </div>

          <div className="text-center">
            <button className="bg-[#24c35b] hover:bg-[#1eaa4e] text-black font-bold px-8 py-4 rounded-lg transition-all duration-300">
              Contact Us for Partnership
            </button>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Join the EKKO v2 <span className="text-[#24c35b]">Ecosystem</span>
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Experience the power of collaboration with our network of partners and be part of the future of decentralized finance.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onLaunchPlatform}
              className="bg-[#24c35b] hover:bg-[#1eaa4e] text-black font-bold px-8 py-4 rounded-lg transition-all duration-300 flex items-center justify-center"
            >
              Launch Platform
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <button 
              className="border border-gray-700 hover:border-[#24c35b]/50 text-white px-8 py-4 rounded-lg transition-all duration-300"
            >
              View All Partners
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Network; 